import { useEffect, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import { Card, DataGrid, DataGridToolbar, useColDefs } from '~components';
import { queryParameters } from '~constants';
import { DataGridToolBarFilterType, StatusVariant } from '~enums';
import { OffsetPagedResults } from '~interfaces';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

import User, { userGridCols } from '../../../interfaces/user';
import Dimens from '../../../themes/dimens';
import UsersService from '../services/usersService';

const service = getService(UsersService);
const persistenceKey = 'users';

interface UsersDataGridProps {
	showOrganisationFilter?: boolean;
	showOrganisationColumn?: boolean;
	showTitle?: boolean;
	showInviteButton?: boolean;
	overwrittenClientId?: string;
	onInviteButtonClicked?: () => void;
	handleInviteButtonClicked?: (mutate: KeyedMutator<OffsetPagedResults<User>>) => void;
	onMutateUpdated?: (mutate: KeyedMutator<OffsetPagedResults<User>>) => void;
}

const UsersDataGrid = ({
	showOrganisationFilter = false,
	showOrganisationColumn = false,
	showTitle = false,
	showInviteButton = false,
	overwrittenClientId,
	handleInviteButtonClicked,
	onMutateUpdated,
}: UsersDataGridProps) => {
	// Hooks & states
	const { t } = useTranslation();
	const location = useLocation();
	const apiRef = useGridApiRef();
	const [searchParams] = useSearchParams();
	const [totalCount, setTotalCount] = useState<number>(0);

	// Data loading
	const cacheKey = service.getUsersCacheKey(searchParams, overwrittenClientId);
	const { data, error, isLoading, isValidating, mutate } = useSWR(
		cacheKey,
		(_args: (string | URLSearchParams | undefined)[], options: { signal?: AbortSignal }) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;
			const filterOrganisationId = searchParams.get(queryParameters.OrganisationId) ?? undefined;
			const role = searchParams.get(queryParameters.Role) ?? undefined;

			return service.getUsers(
				{
					page: page,
					pageSize: pageSize,
					searchQuery: searchQuery,
					filterOrganisationId: overwrittenClientId ?? filterOrganisationId,
					userRole: role,
				},
				options.signal
			);
		},
		{
			onSuccess: (data) => setTotalCount(data?.total || 0),
			keepPreviousData: true,
			use: [cancellationMiddleware],
		}
	);

	// Forward mutate to parent components
	useEffect(() => {
		onMutateUpdated?.(mutate);
	}, [mutate]);

	// Columns
	const colDefs = useColDefs<User>();
	const columns: GridColDef<User>[] = [
		colDefs.gridColDefFor(
			userGridCols.fullName,
			(value: User) => {
				return [[queryParameters.OverwrittenClientId, value.organisationId ?? '']];
			},
			{ from: location.pathname }
		),
		colDefs.gridColDefFor(userGridCols.emailAddress),
		colDefs.gridColDefFor(userGridCols.phoneNumber),
		...(showOrganisationColumn ?
			[colDefs.organisationName(true, (value: User) => value.organisationId ?? '')]
		:	[]),
		colDefs.gridColDefFor(userGridCols.role),
	];

	// Actionbar filter and actions
	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
		{
			type: DataGridToolBarFilterType.UserRole,
		},
		...(showOrganisationFilter ?
			[
				{
					type: DataGridToolBarFilterType.Organisation,
				},
			]
		:	[]),
	];

	const actionBarActions = [
		...(showInviteButton ?
			[
				{
					id: 'inviteUser',
					label: t('user.labels.inviteUser', { ns: 'entities' }),
					variant: StatusVariant.Brand,
					onClick: () => handleInviteButtonClicked?.(mutate),
				},
			]
		:	[]),
	];

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>{t('user.labels.plural', { ns: 'entities' })}</Typography>
				)}
				<DataGridToolbar filters={actionBarFilters} actions={actionBarActions} />
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick
					error={error != null}
					loading={isLoading || isValidating}
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					paginationModel={{
						page: Number(searchParams.get(queryParameters.Page)),
						pageSize: Number(searchParams.get(queryParameters.PageSize)),
					}}
				/>
			</Stack>
		</Card>
	);
};
export default UsersDataGrid;
